import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { AnalyticsApi } from '../../services/api/AnalyticsApi';
import { 
  MetricCard, 
  LoadingChart, 
  EmptyState, 
  DateRangePicker, 
  ExportButton, 
  InsightCard
} from '../../components/analytics/SharedAnalyticsComponents';
import { 
  BarChart, 
  Bar, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const COLORS = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#17a2b8', '#6f42c1', '#20c997', '#fd7e14'];

/**
 * AggregatedMetricsDashboard - Explore event_metrics_hourly table
 * Provides slice-and-dice interface for pre-aggregated analytics data with pivot capabilities.
 */
export default function AggregatedMetricsDashboard() {
  const { user } = useAuth();
  const [dateRange, setDateRange] = useState(() => {
    const endDate = new Date();
    const startDate = new Date(Date.now() - 7 * 86400000);
    return { from: startDate.toISOString(), to: endDate.toISOString() };
  });

  const [dimensions, setDimensions] = useState({
    groupBy: 'event_type', // event_type, category, action, entity_type
    timeBucket: 'hourly', // hourly, daily, weekly
    filters: {
      event_type: '',
      category: '',
      action: '',
      entity_type: ''
    }
  });

  const [data, setData] = useState({
    aggregated: [],
    pivot: [],
    performance: null,
    summary: null
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pivotMode, setPivotMode] = useState(false);
  const [showQueryComparison, setShowQueryComparison] = useState(false);

  const isAdmin = user?.roles?.some(r => r.roleName === 'admin');

  // Fetch real aggregated data from event_metrics_hourly via backend
  const loadAggregatedData = useCallback(async () => {
    if (!isAdmin) return;
    
    setLoading(true);
    setError(null);

    try {
      const result = await AnalyticsApi.getAggregatedEventMetrics({
        from: dateRange.from,
        to: dateRange.to,
        groupBy: dimensions.groupBy,
        timeBucket: dimensions.timeBucket,
        eventType: dimensions.filters.event_type || undefined,
        category: dimensions.filters.category || undefined,
        action: dimensions.filters.action || undefined,
        entityType: dimensions.filters.entity_type || undefined
      });

      // Normalize timeSeries rows for charting
      const timeSeries = (result.timeSeries || []).map(row => ({
        timestamp: row.timestamp,
        event_count: Number(row.eventCount || 0),
        unique_users: Number(row.uniqueUsers || 0),
        unique_sessions: Number(row.uniqueSessions || 0)
      }));

      // Normalize pivot rows
      const pivot = (result.pivot || []).map(row => ({
        name: row.name || '(empty)',
        count: Number(row.count || 0),
        users: Number(row.users || 0),
        sessions: Number(row.sessions || 0)
      }));

      const summary = result.summary || {};

      setData({
        aggregated: timeSeries,
        pivot,
        performance: {
          aggregatedQueryTime: result.performance?.queryTimeMs ?? 0,
          source: result.performance?.source || 'event_metrics_hourly'
        },
        summary: {
          totalEvents: Number(summary.totalEvents || 0),
          uniqueUsers: Number(summary.uniqueUsers || 0),
          distinctEventTypes: Number(summary.distinctEventTypes || 0),
          timeRange: Math.round(Number(summary.timeRangeHours || 0))
        }
      });
    } catch (err) {
      setError(err);
      console.error('Failed to load aggregated metrics:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange, dimensions, isAdmin]);

  useEffect(() => {
    loadAggregatedData();
  }, [loadAggregatedData]);

  const handleDimensionChange = (dimension, value) => {
    setDimensions(prev => ({
      ...prev,
      [dimension]: value
    }));
  };

  const handleFilterChange = (filterKey, value) => {
    setDimensions(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        [filterKey]: value
      }
    }));
  };

  const clearFilters = () => {
    setDimensions(prev => ({
      ...prev,
      filters: {
        event_type: '',
        category: '',
        action: '',
        entity_type: ''
      }
    }));
  };

  const filteredData = data.pivot.filter(item => {
    const activeFilters = Object.entries(dimensions.filters).filter(([_, v]) => v);
    if (activeFilters.length === 0) return true;
    
    // This is a simplification - actual filtering would be more complex
    return true;
  });

  if (!isAdmin) {
    return (
      <div className="settings-container">
        <h2>Aggregated Metrics Explorer</h2>
        <EmptyState message="Admin access required" icon="🔒" />
      </div>
    );
  }

  return (
    <div className="settings-container">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>📊 Aggregated Metrics Explorer</h2>
          <p style={{ fontSize: '.85rem', color: 'var(--text-muted)', margin: '0.25rem 0 0 0' }}>
            Query pre-aggregated hourly metrics for fast analytics
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <DateRangePicker 
            from={dateRange.from} 
            to={dateRange.to} 
            onChange={setDateRange}
          />
          <button onClick={loadAggregatedData} disabled={loading}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#ffebee', border: '1px solid #f44336', borderRadius: '8px', color: '#b71c1c' }}>
          Error loading data: {error.message}
        </div>
      )}

      {/* Query Performance Banner */}
      {!loading && data.performance && showQueryComparison && (
        <InsightCard
          title="⚡ Query Performance"
          description={`Pre-aggregated query completed in ${data.performance.aggregatedQueryTime}ms from ${data.performance.source}`}
          severity="success"
        />
      )}

      {/* KPI Cards */}
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          {[1, 2, 3].map(i => <MetricCard key={i} loading />)}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <MetricCard
            label="Total Events"
            value={data.summary?.totalEvents?.toLocaleString() || '0'}
            subtitle={`${data.summary?.timeRange || 0}h · ${data.summary?.distinctEventTypes || 0} event types`}
            color="#007bff"
          />
          <MetricCard
            label="Unique Users"
            value={data.summary?.uniqueUsers?.toLocaleString() || '0'}
            subtitle="Across all dimensions"
            color="#28a745"
          />
          <MetricCard
            label="Query Speed"
            value={`${data.performance?.aggregatedQueryTime || 0}ms`}
            subtitle={`Source: ${data.performance?.source || 'aggregated'}`}
            color="#17a2b8"
            onClick={() => setShowQueryComparison(!showQueryComparison)}
          />
        </div>
      )}

      {/* Dimension Controls */}
      <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)', marginBottom: '1.5rem' }}>
        <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>⚙️ Dimension Controls</h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              Group By
            </label>
            <select 
              value={dimensions.groupBy}
              onChange={(e) => handleDimensionChange('groupBy', e.target.value)}
              style={{ width: '100%', fontSize: '.8rem' }}
            >
              <option value="event_type">Event Type</option>
              <option value="category">Category</option>
              <option value="action">Action</option>
              <option value="entity_type">Entity Type</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              Time Bucket
            </label>
            <select 
              value={dimensions.timeBucket}
              onChange={(e) => handleDimensionChange('timeBucket', e.target.value)}
              style={{ width: '100%', fontSize: '.8rem' }}
            >
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              Display Mode
            </label>
            <select 
              value={pivotMode ? 'pivot' : 'raw'}
              onChange={(e) => setPivotMode(e.target.value === 'pivot')}
              style={{ width: '100%', fontSize: '.8rem' }}
            >
              <option value="raw">Time Series</option>
              <option value="pivot">Pivot Table</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              Filter: Event Type
            </label>
            <input 
              type="text"
              value={dimensions.filters.event_type}
              onChange={(e) => handleFilterChange('event_type', e.target.value)}
              placeholder="e.g., page_view"
              style={{ width: '100%', fontSize: '.8rem' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              Filter: Category
            </label>
            <input 
              type="text"
              value={dimensions.filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              placeholder="e.g., navigation"
              style={{ width: '100%', fontSize: '.8rem' }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button 
              className="secondary" 
              onClick={clearFilters}
              style={{ width: '100%' }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Main Visualization */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Chart */}
        <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '.95rem' }}>
              {pivotMode ? 'Distribution' : 'Time Series'}
            </h3>
            <ExportButton data={pivotMode ? filteredData : data.aggregated} filename="aggregated-metrics" />
          </div>
          
          {loading ? (
            <LoadingChart height={350} />
          ) : filteredData.length === 0 ? (
            <EmptyState message="No data for selected dimensions" />
          ) : pivotMode ? (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={filteredData.slice(0, 20)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  style={{ fontSize: '.65rem' }}
                />
                <YAxis style={{ fontSize: '.7rem' }} />
                <Tooltip contentStyle={{ fontSize: '.75rem' }} />
                <Legend wrapperStyle={{ fontSize: '.75rem' }} />
                <Bar dataKey="count" fill="#007bff" name="Event Count" />
                <Bar dataKey="users" fill="#28a745" name="Unique Users" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={data.aggregated.slice(-100)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="timestamp" 
                  tickFormatter={(v) => new Date(v).toLocaleDateString()}
                  style={{ fontSize: '.7rem' }}
                />
                <YAxis style={{ fontSize: '.7rem' }} />
                <Tooltip 
                  labelFormatter={(v) => new Date(v).toLocaleString()}
                  contentStyle={{ fontSize: '.75rem' }}
                />
                <Legend wrapperStyle={{ fontSize: '.75rem' }} />
                <Line type="monotone" dataKey="event_count" stroke="#007bff" strokeWidth={2} name="Events" dot={false} />
                <Line type="monotone" dataKey="unique_users" stroke="#28a745" strokeWidth={2} name="Users" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Distribution Pie */}
        <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>Distribution by {dimensions.groupBy}</h3>
          {loading ? (
            <LoadingChart height={350} />
          ) : filteredData.length === 0 ? (
            <EmptyState message="No data" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={filteredData.slice(0, 8)}
                  dataKey="count"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={(entry) => `${entry.name}: ${entry.count}`}
                  labelLine={false}
                >
                  {filteredData.slice(0, 8).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: '.75rem' }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Data Table */}
      <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '.95rem' }}>Pivot Table</h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <ExportButton data={filteredData} filename="pivot-table" format="csv" />
            <ExportButton data={filteredData} filename="pivot-table" format="json" />
          </div>
        </div>
        
        {loading ? (
          <LoadingChart height={200} />
        ) : filteredData.length === 0 ? (
          <EmptyState message="No data to display" />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.75rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>{dimensions.groupBy}</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Event Count</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Unique Users</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Events/User</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.slice(0, 50).map((row, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td style={{ padding: '0.5rem' }}>{row.name}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{row.count.toLocaleString()}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{row.users.toLocaleString()}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{(row.count / row.users).toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}
